import React, { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { useAuth } from '../../Auth/AuthProvider'
import { db } from '../../firebase'
import ProfilePic from '../../Components/ProfilePic/ProfilePic'
import { Button } from '@/Components/ui/button'
import { Input } from '@/Components/ui/input'
import { Label } from '@/Components/ui/label'

export default function ProfileSection() {
  const { uid, displayName, email, photoURL, updateDisplayName } = useAuth()
  const [name, setName] = useState('')
  const [savedName, setSavedName] = useState('')
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function loadProfileName() {
      setLoadingProfile(true)

      if (displayName) {
        if (!cancelled) {
          setName(displayName)
          setSavedName(displayName)
          setLoadingProfile(false)
        }
        return
      }

      if (!uid) {
        if (!cancelled) setLoadingProfile(false)
        return
      }

      try {
        const snap = await getDoc(doc(db, 'users', uid))
        if (cancelled) return
        const fromDb = snap.exists() ? snap.data()?.displayName : null
        const next = typeof fromDb === 'string' ? fromDb : ''
        setName(next)
        setSavedName(next)
      } catch (err) {
        console.error('Failed to load profile', err)
      } finally {
        if (!cancelled) setLoadingProfile(false)
      }
    }

    loadProfileName()
    return () => {
      cancelled = true
    }
  }, [uid, displayName])

  const trimmed = name.trim()
  const canSave =
    !loadingProfile &&
    trimmed.length >= 1 &&
    trimmed.length <= 100 &&
    trimmed !== savedName

  const handleSave = async (event) => {
    event.preventDefault()
    if (!canSave || saving) return

    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      await updateDisplayName(trimmed)
      setName(trimmed)
      setSavedName(trimmed)
      setMessage('Display name updated.')
    } catch (err) {
      console.error('Failed to update display name', err)
      setError(err?.message || 'Could not update display name.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="space-y-4 border-b border-mauve-200 pb-8">
      <div>
        <h2 className="text-lg font-semibold text-mauve-900">Profile</h2>
        <p className="mt-1 text-sm text-mauve-600">
          Your name appears in the app menus. Email cannot be changed here.
        </p>
      </div>

      <div className="flex items-center gap-4">
        <ProfilePic size={64} />
        <p className="text-sm text-mauve-600">
          {photoURL
            ? 'Photo comes from your sign-in provider.'
            : 'Photo is loaded from Gravatar using your email.'}
        </p>
      </div>

      <form className="grid max-w-md gap-3" onSubmit={handleSave}>
        <div className="grid gap-1.5">
          <Label htmlFor="account-display-name">Display name</Label>
          <Input
            id="account-display-name"
            type="text"
            value={name}
            maxLength={100}
            disabled={saving || loadingProfile}
            onValueChange={setName}
            autoComplete="name"
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="account-email">Email</Label>
          <Input
            id="account-email"
            type="email"
            value={email ?? ''}
            disabled
            readOnly
          />
        </div>

        {message ? <p className="text-sm text-green-700">{message}</p> : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div>
          <Button type="submit" disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Save name'}
          </Button>
        </div>
      </form>
    </section>
  )
}
